/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Oct 11 2024
 **/

import { ComponentProps, useCallback, useState } from "react";
import { useTheme } from "@emotion/react";
import { isEmpty } from "lodash-es";
import cx from "classnames";
import { timer } from "rxjs";

import { Button, Icon, Typography } from "@/frameworks/designStuff/components";
import { ChronoUtils } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { MessageList } from "@/frameworks/core/chat";
import { useChat } from "@/frameworks/core/API";

import * as S from "./emotion";

type VoteNotice = {
	vote: MessageList[number]["vote"];
	userName: string;
	roomID: string;
	variant: ChatUI.Size;
};

type VoteItems = {
	list: string[];
};

export default function VoteNotice({ vote, roomID, variant, userName }: VoteNotice) {
	const chatAPI = useChat();
	const { colors } = useTheme();
	const [isOpened, setIsOpened] = useState(false);
	const [selectedItems, setSelectedItems] = useState<VoteItems>({
		list: [] as string[]
	});

	const ArrowButton = ({ name }: { name: ComponentProps<typeof Button.Icon>["name"] }) => {
		return (
			<Button.Icon
				size="small"
				name={name}
				filled="transparent"
				variant="contained"
				css={{
					width: "16px",
					height: "16px"
				}}
				onClick={handleShow}
			/>
		);
	};

	const handleClick = (voteItemID: string) => {
		setSelectedItems((prev) => {
			if (vote?.allowMultipleChoice) {
				return {
					...prev,
					list: isEmpty(selectedItems.list?.find((id) => id === voteItemID))
						? prev.list.concat(voteItemID)
						: prev.list.filter((id) => id !== voteItemID)
				};
			}
			return {
				...prev,
				list: isEmpty(selectedItems.list?.find((id) => id === voteItemID)) ? [voteItemID] : []
			};
		});
	};

	const handleShow = useCallback(async () => {
		setIsOpened(!isOpened);
	}, [isOpened]);

	const scrollToTarget = (e: React.MouseEvent) => {
		e.stopPropagation();

		if (!vote?.voteId) return;

		const targetElement = document.getElementById(vote.voteId);
		if (targetElement) {
			targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	};

	const handleSubmit = useCallback(async () => {
		await chatAPI.vote.choose.execute({
			roomId: roomID,
			voteId: String(vote?.voteId),
			choices: selectedItems.list.map((votedItemID) => {
				return {
					voteItemId: votedItemID,
					choice: true
				};
			})
		});
		const targetElement = document.getElementById(String(vote?.voteId));

		if (targetElement) {
			targetElement.scrollIntoView({ behavior: "smooth" });
			targetElement.classList.add("flash-animation");

			timer(2000).subscribe(() => {
				targetElement.classList.remove("flash-animation");
			});
		}
	}, [selectedItems]);

	return (
		<S.VoteNoticeLayout gap={8} horizonAlign="distribute" onClick={handleShow}>
			<S.VoteNoticeTitle verticalAlign="center" horizonAlign="distribute">
				<S.TitleArea verticalAlign="center" gap={8}>
					<Icon name="Vote24" size={16} />
					<Typography.p>{vote?.title}</Typography.p>
				</S.TitleArea>
				<Maybe test={variant === "transparent"} fallback={<ArrowButton name={isOpened ? "Up24" : "Down24"} />}>
					<ArrowButton name={isOpened ? "Down24" : "Up24"} />
				</Maybe>
			</S.VoteNoticeTitle>
			<Maybe test={isOpened}>
				<S.VoteNoticeWrapper onClick={(e) => scrollToTarget(e)}>
					<S.VoteNoticeNameField gap={8} verticalAlign="center">
						<Typography.p typoSize="Button1" typoColor="neutral_80_100">
							{userName}
						</Typography.p>
						<Typography typoSize="Body4" typoColor="neutral_60_100">
							{ChronoUtils.formatDateAgoMessage(Number(vote?.deadline))}{" "}
							{ChronoUtils.formatTimestamp12Hours(Number(vote?.deadline))}
						</Typography>
					</S.VoteNoticeNameField>
					<S.VoteNoticeList gap={4}>
						{vote?.items.map((item) => {
							return (
								<S.VoteNoticeItem
									key={item.voteItemId}
									onClick={() => handleClick(item.voteItemId)}
									className={cx({
										selected: Boolean(selectedItems.list?.find((id) => id === item.voteItemId))
									})}
								>
									<Typography.p typoSize="Body3_MD" typoColor="neutral_80_100">
										{item.content}
									</Typography.p>
								</S.VoteNoticeItem>
							);
						})}
					</S.VoteNoticeList>
					<S.CountAreaWrapper
						verticalAlign="center"
						horizonAlign={vote?.allowMultipleChoice ? "distribute" : "right"}
					>
						<Maybe test={vote?.allowMultipleChoice}>
							<Typography typoSize="Body5" typoColor="neutral_80_100">
								{_T("복수 선택 가능")}
							</Typography>
						</Maybe>
						<Maybe
							test={vote?.allowAnonymous && vote?.voterCount > 0}
							fallback={
								<Typography typoSize="Body5" typoColor="neutral_80_100">
									{_T("{{count}}명 참여", { count: vote?.voterCount })}
								</Typography>
							}
						/>
					</S.CountAreaWrapper>
					<S.DeadlineAreaWrapper gap={2} verticalAlign="center" horizonAlign="left">
						<Icon name="Clock24" size={16} fill={colors.neutral_60_100} />
						<Typography typoSize="Body5" typoColor="neutral_80_100">
							{ChronoUtils.formatDate(Number(vote?.deadline), true)}{" "}
							{ChronoUtils.formatTimestamp12Hours(Number(vote?.deadline))} {_T("종료")}
						</Typography>
					</S.DeadlineAreaWrapper>

					<Button
						size="small"
						typoSize="Body3_MD"
						onClick={handleSubmit}
						disabled={Boolean(selectedItems?.list.length === 0)}
					>
						{_T("투표 완료")}
					</Button>
				</S.VoteNoticeWrapper>
			</Maybe>
		</S.VoteNoticeLayout>
	);
}
